import { Card, Skeleton, Typography } from 'antd';
import type { IconSvgElement } from '@hugeicons/react';
import { Icon } from '../Icon/Icon.js';
import { space, tone as toneOf, typography, type SemanticTone } from '@/design/tokens.js';

const { Text } = Typography;

export interface StatCardProps {
  label: string;
  value: string | number;
  /** Context under the number: "of 12 days", "this month". */
  hint?: string;
  icon?: IconSvgElement;
  tone?: SemanticTone;
  loading?: boolean;
  /** Makes the whole card a button. Only set this if there is somewhere to go. */
  onClick?: () => void;
}

/**
 * Display size for the value.
 *
 * The card is built for numbers, which are short. A word or phrase — "Not
 * checked in", "On leave" — set at the same 24px wraps to three cramped lines
 * and makes a row of cards ragged. Long text steps down a size and relaxes its
 * line height instead.
 *
 * Auto-detected rather than a prop, so no caller has to remember it and no two
 * screens disagree about where the threshold is.
 */
const LONG_VALUE_THRESHOLD = 10;

function valueTypography(value: string | number) {
  const isLongText = typeof value === 'string' && value.length > LONG_VALUE_THRESHOLD;
  return isLongText
    ? { fontSize: typography.size.lg, lineHeight: typography.lineHeight.snug }
    : { fontSize: typography.size['2xl'], lineHeight: typography.lineHeight.tight };
}

/**
 * A single number, prominently.
 *
 * Used for leave balances, attendance summaries and pending-approval counts.
 * The number is the largest thing on the card, in tabular figures so a row of
 * cards lines up.
 *
 * The value is `aria-live="polite"` because these update in place — a balance
 * dropping after a leave application should be announced, not silently changed
 * under a screen-reader user.
 */
export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  loading = false,
  onClick,
}: StatCardProps) {
  const palette = toneOf(tone);
  const interactive = Boolean(onClick);

  return (
    <Card
      size="small"
      variant="outlined"
      hoverable={interactive}
      onClick={onClick}
      // A clickable card must be reachable and operable by keyboard, not just
      // by mouse. antd's Card gives us neither for free.
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      styles={{ body: { padding: space[4] } }}
      style={{ height: '100%', cursor: interactive ? 'pointer' : undefined }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: space[3] }}>
        {icon ? (
          <span
            aria-hidden
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 36,
              height: 36,
              borderRadius: 8,
              backgroundColor: palette.bg,
              color: palette.fg,
              flexShrink: 0,
            }}
          >
            <Icon icon={icon} size="lg" />
          </span>
        ) : null}

        <div style={{ minWidth: 0, flex: 1 }}>
          <Text
            type="secondary"
            style={{
              display: 'block',
              fontSize: typography.size.sm,
              fontWeight: typography.weight.medium,
            }}
          >
            {label}
          </Text>

          {loading ? (
            <Skeleton.Input active size="small" style={{ width: 72, marginTop: space[2] }} />
          ) : (
            <div
              aria-live="polite"
              className="orbit-numeric"
              style={{
                ...valueTypography(value),
                fontWeight: typography.weight.semibold,
                letterSpacing: typography.letterSpacing.tight,
                marginTop: space[1],
                color: tone === 'neutral' ? 'var(--ant-color-text)' : palette.fg,
                // Never let a long value push the card wider than its column.
                overflowWrap: 'anywhere',
              }}
            >
              {value}
            </div>
          )}

          {hint && !loading ? (
            <Text type="secondary" style={{ fontSize: typography.size.sm }}>
              {hint}
            </Text>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
